import React, { useEffect, useState, useRef } from "react";
import InputField from "../misc/InputField";
import { Button } from "../misc/Button";
import CustomSelect from "../misc/CustomSelect";
import { TIME_ZONES, TimeZoneOption } from "../onboarding/utils/timeZones";
import { ActionMeta, SingleValue } from "react-select";
import { OptionType } from "../../types/option-type";
import PasswordChangeOverlay from "./PasswordChangeOverlay";
import { RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import axios from "axios";
import { UpdatePersonalInfoDTO } from "./dto/update-personal-info.dto";
import { getAuthHeader } from "../../utils/authHeader";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import { setUser } from "../../store/authSlice";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ImageIcon } from "lucide-react";
import { Card, CardHeader } from "../misc/Card";
import { CardContent } from "../calendar/Card";
import { useIsMobile } from "../../hooks/useIsMobile";
import { isValidPhoneNumber } from "../../utils/validationUtil";
import {
    getImageDimensions,
    CORPORATE_HEADSHOT_MIN_DIMENSION,
} from "../../utils/imageValidation";
import SimpleTooltip from "../misc/SimpleTooltip";
import { useImpersonation } from "../../hooks/useImpersonation";
import ReconfirmOverlay from "../misc/ReconfirmOverlay";
import CorporateHeadshotCropModal from "./CorporateHeadshotCropModal";
import { clearImage } from "../../utils/dexieImageCache";
import { User } from "../../types/user";
import { cn } from "../../utils/classMerger";

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Backend response from /settings/profile-image does not carry the Firebase Auth
// fields the UI relies on (photoURL, providerId, uid, displayName, emailVerified).
// Re-attach them from the current Redux user so we don't lose them on dispatch.
const mergeFirebaseFields = (
    updatedUser: User,
    currentUser: User | null
): User => ({
    ...updatedUser,
    photoURL: currentUser?.photoURL,
    providerId: currentUser?.providerId ?? "",
    uid: currentUser?.uid ?? "",
    displayName: currentUser?.displayName ?? null,
    emailVerified: currentUser?.emailVerified ?? false,
});

const PersonalInformation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Retrieve user from Redux store
    const user = useSelector((state: RootState) => state.auth.user);
    const originalUserRef = useRef(user); // Store original user data

    // Initialize input fields with current user data
    const [nameFirst, setNameFirst] = useState<string>(user?.nameFirst || "");
    const [nameLast, setNameLast] = useState<string>(user?.nameLast || "");
    const [phone, setPhone] = useState(user?.phoneNumber || "");
    const [timeZone, setTimeZone] = useState(user?.timeZone || "");
    const [isPasswordOverlayOpen, setIsPasswordOverlayOpen] = useState(false);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const [signUpUser, setSignUpUser] = useState<boolean>(false);
    const [email, setEmail] = useState<string>(user?.email || "");
    const [companyName, setCompanyName] = useState<string>(
        user?.companyName || ""
    );
    const [licenseNumber, setLicenseNumber] = useState<string>(
        user?.licenseNumber || ""
    );
    const isImpersonated = useImpersonation();

    // Corporate Headshot states
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [pendingCropFile, setPendingCropFile] = useState<File | null>(null);
    const [autoVideoOn, setAutoVideoOn] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasCustomPhoto = !!user?.corporateHeadshot;

    // Update signUpUser and originalUserRef when user changes
    useEffect(() => {
        setSignUpUser(user?.providerId === "password");
        originalUserRef.current = user; // Update original user data if user changes

        // Initialize input fields when user data changes
        setNameFirst(user?.nameFirst || "");
        setNameLast(user?.nameLast || "");
        setPhone(user?.phoneNumber || "");
        setTimeZone(user?.timeZone || "");
    }, [user]);

    // Whether auto-video is currently on — the headshot is required for it, so
    // removing the headshot turns it off. We surface that in the confirm dialog.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const headers = await getAuthHeader();
                const { data } = await axios.get(
                    `${backendUrl}/settings/branding`,
                    { headers }
                );
                if (!cancelled) {
                    setAutoVideoOn(Boolean(data?.autoGenerateSlideshow));
                }
            } catch {
                // Non-blocking — fall back to the generic removal message.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [backendUrl]);

    const formatPhoneNumber = (phoneNumber: string | null | undefined) => {
        if (!phoneNumber) return "Ex: (878) 231-6060";
        const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, "US");
        return parsedPhoneNumber
            ? parsedPhoneNumber.formatNational()
            : phoneNumber;
    };

    const getDefaultTimeZone = (): TimeZoneOption | null => {
        const current: TimeZoneOption | undefined = TIME_ZONES.find(
            (tz: TimeZoneOption) => tz.value === timeZone
        );
        if (current) return current;

        const browserTZ = TIME_ZONES.find(
            (tz: TimeZoneOption) => tz.value === BROWSER_TIMEZONE
        );

        return browserTZ || null;
    };

    const handleTimeZoneChange = (
        option: SingleValue<OptionType>,
        actionMeta: ActionMeta<OptionType>
    ) => {
        setTimeZone(option ? option.value : "");
    };

    const saveChangesHandler = async () => {
        // Construct the update DTO with only modified fields
        const updateDTO: UpdatePersonalInfoDTO = {};

        if (
            nameFirst.trim() !== (originalUserRef.current?.nameFirst || "")
        ) {
            updateDTO.nameFirst = nameFirst.trim();
        }

        if (nameLast.trim() !== (originalUserRef.current?.nameLast || "")) {
            updateDTO.nameLast = nameLast.trim();
        }

        if (
            phone.trim() &&
            phone.trim() !== originalUserRef.current?.phoneNumber
        ) {
            updateDTO.phoneNumber = phone.trim();
        }

        if (timeZone && timeZone !== originalUserRef.current?.timeZone) {
            updateDTO.timeZone = timeZone;
        }

        if (
            companyName &&
            companyName !== originalUserRef.current?.companyName
        ) {
            updateDTO.companyName = companyName;
        }

        if (
            licenseNumber &&
            licenseNumber !== originalUserRef.current?.licenseNumber
        ) {
            updateDTO.licenseNumber = licenseNumber;
        }

        // If no fields have been modified, do nothing
        if (Object.keys(updateDTO).length === 0) {
            return;
        }

        // Check if phone number is valid if it's being updated
        if (
            updateDTO.phoneNumber &&
            !isValidPhoneNumber(updateDTO.phoneNumber)
        ) {
            toast.error(
                <CustomToast message={"Invalid phone number"} type={"error"} />
            );
            return;
        }

        const header = await getAuthHeader();

        try {
            await axios.patch(
                `${backendUrl}/settings/personal-info`,
                updateDTO,
                {
                    headers: header,
                }
            );

            const response = await axios.get(
                `${backendUrl}/settings/personal-info`,
                {
                    headers: header,
                }
            );

            dispatch(setUser(response.data)); // Update Redux store

            // Update the original user data
            originalUserRef.current = response.data;

            toast.success(
                <CustomToast
                    message={"Personal information updated successfully"}
                    type={"success"}
                />
            );
        } catch (error) {
            toast.error(
                <CustomToast
                    message={"Error updating personal information"}
                    type={"error"}
                />
            );
        }
    };

    // Profile picture file selection handler (validates and uploads directly)
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        // Reset the input value immediately so the same file can be re-picked
        // after a failed validation (otherwise onChange won't fire next time).
        if (fileInputRef.current) fileInputRef.current.value = "";

        const validTypes = ["image/jpeg", "image/png", "image/webp"];
        if (!validTypes.includes(file.type)) {
            toast.error(
                <CustomToast
                    message="Only JPEG, PNG, and WebP images are supported"
                    type="error"
                />
            );
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5 MB
        if (file.size === 0) {
            toast.error(<CustomToast message="File is empty" type="error" />);
            return;
        }
        if (file.size > maxSize) {
            toast.error(
                <CustomToast
                    message="Image must be less than 5 MB"
                    type="error"
                />
            );
            return;
        }

        const ext = file.name.split(".").pop()?.toLowerCase();
        const validExtensions = ["jpg", "jpeg", "png", "webp"];
        if (!ext || !validExtensions.includes(ext)) {
            toast.error(
                <CustomToast
                    message="Invalid file extension. Use JPG, PNG, or WebP"
                    type="error"
                />
            );
            return;
        }

        const mimeToExt: Record<string, string[]> = {
            "image/jpeg": ["jpg", "jpeg"],
            "image/png": ["png"],
            "image/webp": ["webp"],
        };

        if (!mimeToExt[file.type]?.includes(ext)) {
            toast.error(
                <CustomToast
                    message="File extension does not match image type"
                    type="error"
                />
            );
            return;
        }

        try {
            const { width, height } = await getImageDimensions(file);
            if (
                width < CORPORATE_HEADSHOT_MIN_DIMENSION ||
                height < CORPORATE_HEADSHOT_MIN_DIMENSION
            ) {
                toast.error(
                    <CustomToast
                        message="Please upload a higher-resolution photo so it looks sharp in your generated videos."
                        type="error"
                    />
                );
                return;
            }
        } catch {
            toast.error(
                <CustomToast
                    message="Could not read image. Try a different file."
                    type="error"
                />
            );
            return;
        }

        // Source validated — hand off to the crop modal. Actual upload happens
        // in handleCropConfirm once the user picks the sub-region.
        setPendingCropFile(file);
    };

    // Upload the cropped JPEG blob produced by CorporateHeadshotCropModal
    const handleCropConfirm = async (blob: Blob) => {
        setPendingCropFile(null);
        setIsUploading(true);
        const cacheKey = user?._id ? `corporateHeadshot-${user._id}` : undefined;

        try {
            const headers = await getAuthHeader();

            // Step 1: Get signed upload URL (cropped output is always JPEG)
            const {
                data: { signedUrl, key },
            } = await axios.post(
                `${backendUrl}/settings/corporate-headshot/signed-url`,
                {
                    contentType: "image/jpeg",
                    extension: "jpg",
                    fileSize: blob.size,
                },
                { headers }
            );

            // Step 2: Upload directly to GCS (raw axios — auth headers would
            // break the signed-URL upload)
            await axios.put(signedUrl, blob, {
                headers: { "Content-Type": "image/jpeg" },
            });

            // Step 3: Confirm upload to backend
            const { data: updatedUser } = await axios.post(
                `${backendUrl}/settings/corporate-headshot`,
                { key },
                { headers }
            );

            // Invalidate any previously cached blob so downstream consumers
            // (e.g. the slideshow renderer) re-fetch under the same per-user key.
            if (cacheKey) {
                try {
                    await clearImage(cacheKey);
                } catch {
                    // Cache miss is harmless; the next fetch will repopulate.
                }
            }

            dispatch(setUser(mergeFirebaseFields(updatedUser, user)));

            toast.success(
                <CustomToast
                    message="Corporate headshot uploaded successfully"
                    type="success"
                />
            );
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to upload corporate headshot"
                    type="error"
                />
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleCropCancel = () => {
        setPendingCropFile(null);
    };

    // Corporate headshot removal
    const handleRemovePhoto = async () => {
        if (isRemoving) return; // Prevent multiple clicks

        // Close dialog immediately to prevent UI hang
        setShowRemoveConfirm(false);
        setIsRemoving(true);
        const cacheKey = user?._id ? `corporateHeadshot-${user._id}` : undefined;

        try {
            const headers = await getAuthHeader();

            const { data: updatedUser } = await axios.delete(
                `${backendUrl}/settings/corporate-headshot`,
                { headers }
            );

            if (cacheKey) {
                try {
                    await clearImage(cacheKey);
                } catch {
                    // Cache miss is harmless.
                }
            }

            dispatch(setUser(mergeFirebaseFields(updatedUser, user)));

            // Backend turns the master auto-video toggle off when the headshot
            // (a required input) is removed — reflect that locally.
            setAutoVideoOn(false);

            toast.success(
                <CustomToast message="Corporate headshot removed" type="success" />
            );
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to remove corporate headshot"
                    type="error"
                />
            );
        } finally {
            setIsRemoving(false);
        }
    };

    const isMobile = useIsMobile(1200);

    return (
        <div
            className={`flex justify-center ${isMobile ? "h-full" : "mt-4"}  `}
        >
            <Card
                className={`w-full max-w-4xl mx-auto ${
                    isMobile ? "h-full m-0 rounded-none" : "h-auto"
                }`}
            >
                <div className="p-4">
                    <Button
                        id="back-to-settings-btn"
                        onClick={() => navigate("/settings")}
                        className="mb-4 flex items-center"
                    >
                        <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                    </Button>
                </div>
                <CardHeader>
                    <h2 className="text-2xl font-bold text-primary">
                        Personal Information
                    </h2>
                </CardHeader>

                <CardContent className="p-0 px-4 sm:px-10 pb-6 sm:pb-10">
                    {/* Corporate Headshot Section */}
                    <div className="mt-6 mb-6">
                        <label className="block text-sm font-medium text-[#193456] mb-1">
                            Corporate Headshot
                        </label>
                        <p className="text-xs text-slate-500 mb-3">
                            Featured in your generated videos.
                        </p>
                        <div className="flex items-start gap-4">
                            <div className="h-20 w-20 flex-shrink-0 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                                {hasCustomPhoto && user?.corporateHeadshot ? (
                                    <img
                                        src={user.corporateHeadshot}
                                        alt="Corporate headshot"
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <ImageIcon
                                        className="w-8 h-8 text-slate-300"
                                        strokeWidth={1.5}
                                    />
                                )}
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={handleFileSelect}
                                disabled={isUploading || isImpersonated}
                                className="hidden"
                                aria-label="Upload corporate headshot"
                            />

                            <div className="flex flex-col gap-2 min-w-0">
                                <div className="flex gap-2 items-center flex-wrap">
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={
                                            isImpersonated ||
                                            isRemoving ||
                                            isUploading
                                        }
                                        className={cn(
                                            "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                                            (isRemoving || isUploading) &&
                                                "opacity-50 cursor-not-allowed"
                                        )}
                                        aria-label={
                                            hasCustomPhoto
                                                ? "Change corporate headshot"
                                                : "Upload corporate headshot"
                                        }
                                    >
                                        {isUploading
                                            ? "Uploading..."
                                            : hasCustomPhoto
                                            ? "Change"
                                            : "Upload"}
                                    </button>
                                    {hasCustomPhoto && (
                                        <button
                                            onClick={() => setShowRemoveConfirm(true)}
                                            disabled={
                                                isImpersonated ||
                                                isRemoving ||
                                                isUploading
                                            }
                                            className={cn(
                                                "text-[#d50000] hover:text-[#c62828] text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                                                isRemoving && "opacity-60"
                                            )}
                                            aria-label="Remove corporate headshot"
                                        >
                                            {isRemoving ? "Removing..." : "Remove"}
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-slate-500">
                                    JPG, PNG or WEBP · Use a clear, high-resolution photo · Max 5 MB
                                </p>
                            </div>
                        </div>
                        {isImpersonated && (
                            <p className="text-xs text-slate-500 mt-2">
                                Corporate headshot cannot be changed while
                                impersonating
                            </p>
                        )}
                    </div>

                    <div className="border-b border-gray-200 mb-6"></div>

                    <div>
                        <SimpleTooltip
                            message="Please contact support to change your email"
                            visible={true}
                            className="w-full"
                        >
                            <InputField
                                label="Email"
                                placeholder="Email"
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={true}
                            />
                        </SimpleTooltip>
                        <span className="text-neutral-500 text-sm block mt-1">
                            Please contact us via{" "}
                            <strong
                                className="text-primary hover:text-secondary cursor-pointer"
                                onClick={() => {
                                    navigate("/help", {
                                        state: { tab: "contact" },
                                    });
                                }}
                            >
                                Help
                            </strong>{" "}
                            to change your email.
                        </span>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField
                            label="First Name"
                            placeholder="First Name"
                            type="text"
                            value={nameFirst}
                            onChange={(e) => setNameFirst(e.target.value)}
                            disabled={isImpersonated}
                        />
                        <InputField
                            label="Last Name"
                            placeholder="Last Name"
                            type="text"
                            value={nameLast}
                            onChange={(e) => setNameLast(e.target.value)}
                            disabled={isImpersonated}
                        />
                    </div>
                    <div className="mt-4">
                        <InputField
                            label="Phone Number"
                            placeholder={formatPhoneNumber(user?.phoneNumber)}
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={isImpersonated}
                        />
                    </div>
                    {user?.mlsApproved && (
                        <div className="mt-4">
                            <InputField
                                label="License Number"
                                placeholder={
                                    user?.licenseNumber || "Ex: AB1234567"
                                }
                                type="text"
                                value={licenseNumber}
                                onChange={(e) =>
                                    setLicenseNumber(e.target.value)
                                }
                                disabled={isImpersonated}
                            />
                        </div>
                    )}
                    <div className="mt-4">
                        <CustomSelect
                            id="time-zone-select-dropdown"
                            label="Time Zone"
                            options={TIME_ZONES}
                            value={getDefaultTimeZone()}
                            onChange={handleTimeZoneChange}
                            placeholder={
                                TIME_ZONES.find(
                                    (tz) => tz.value === user?.timeZone
                                )?.label
                            }
                            className="w-full md:w-1/2 min-h-[60px]"
                            isDisabled={isImpersonated}
                        />
                        <span className="text-neutral-500 text-sm">
                            Time zone changes will only take effect upon
                            campaign renewal or when creating a new campaign.
                        </span>
                    </div>
                    <div className="mt-4">
                        <SimpleTooltip
                            message="Please contact support to change your company name"
                            visible={user?.mlsApproved || false}
                            className="w-full"
                        >
                            <InputField
                                label="Company Name"
                                placeholder="Company Name"
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                disabled={user?.mlsApproved || isImpersonated || false}
                            />
                        </SimpleTooltip>
                        <span className="text-neutral-500 text-sm block mt-1">
                            Please contact us via{" "}
                            <strong
                                className="text-primary hover:text-secondary cursor-pointer"
                                onClick={() => {
                                    navigate("/help", {
                                        state: { tab: "contact" },
                                    });
                                }}
                            >
                                Help
                            </strong>{" "}
                            to change your company name.
                        </span>
                    </div>
                    <div className="flex flex-row items-center justify-between gap-4 mt-6">
                        {signUpUser && (
                            <Button
                                variant="secondary"
                                onClick={() => setIsPasswordOverlayOpen(true)}
                                disabled={isImpersonated}
                            >
                                Change Password
                            </Button>
                        )}
                        <div className="">
                            <Button onClick={saveChangesHandler} disabled={isImpersonated}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </CardContent>
                <PasswordChangeOverlay
                    isOpen={isPasswordOverlayOpen}
                    onClose={() => setIsPasswordOverlayOpen(false)}
                />

                <ReconfirmOverlay
                    isOpen={showRemoveConfirm}
                    title="Remove corporate headshot?"
                    message={
                        autoVideoOn
                            ? "Your corporate headshot is required for auto-generated listing videos. Removing it will turn off auto-video on your account — you can re-enable it after adding a headshot again."
                            : "Your corporate headshot will be removed and won't appear in newly generated videos. You can upload a new one anytime."
                    }
                    confirmText="Remove"
                    reverse={true}
                    onConfirm={handleRemovePhoto}
                    onClose={() => setShowRemoveConfirm(false)}
                />

                {pendingCropFile && (
                    <CorporateHeadshotCropModal
                        file={pendingCropFile}
                        onConfirm={handleCropConfirm}
                        onCancel={handleCropCancel}
                    />
                )}
            </Card>
        </div>
    );
};

export default PersonalInformation;
